import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useBusiness } from "../../../Common/Hooks/useBusiness";
import { useDueDiligence } from "../../../Common/Hooks/useDueDiligence";
import { DueDiligenceCase } from "../../../Common/Types-DTOs/DueDiligence";
import { formatMoney } from "../../../Common/Utils/dueDiligencePlans";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";

const Offer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const Note = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #115e59;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
`;

const ApplyButton = styled.button`
  padding: 0.5rem 1.25rem;
  border: 1px solid #0d9488;
  border-radius: 0.5rem;
  background-color: white;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0d9488;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #f0fdfa;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

interface CreditDiscountProps {
  caseId: string;
  /** The credits covered its whole price: the case comes back paid. */
  onPaid: (paidCase: DueDiligenceCase) => void;
}

/**
 * Without a subscription, whole credits come off what a DD pending payment
 * costs in euros, each worth its euros. Applying them spends them there and
 * then; the card pays the rest.
 */
export const CreditDiscount = ({ caseId, onPaid }: CreditDiscountProps) => {
  const { t, i18n } = useTranslation();
  const { business, resyncBusiness } = useBusiness();
  const { getCreditDiscount, applyCreditDiscount } = useDueDiligence();
  const [discount, setDiscount] = useState<{ credits: number; amount: number; creditsToApply: number } | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const credits = business?.credits;

  useEffect(() => {
    let cancelled = false;
    getCreditDiscount(caseId).then((result) => {
      if (!cancelled && !isErr(result)) setDiscount(result.value);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  if (discount === null) return null;

  if (discount.credits > 0) {
    return (
      <Note>
        {t("dueDiligence.creditDiscount.applied", {
          count: discount.credits,
          amount: formatMoney(discount.amount, "eur", i18n.language),
        })}
      </Note>
    );
  }

  // Subscribers pay with credits at their own price, not as a discount
  if (!credits || credits.dueDiligencePlan || credits.balance <= 0) return null;

  const apply = async () => {
    if (isApplying) return;
    setIsApplying(true);
    const result = await applyCreditDiscount(caseId);
    setIsApplying(false);
    resyncBusiness();
    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          t(
            result.error.errorCode === "INSUFFICIENT_CREDITS"
              ? "dueDiligence.creditDiscount.errors.notEnough"
              : "dueDiligence.creditDiscount.errors.failed"
          )
        )
      );
      return;
    }
    if (result.value.case.status !== "pending_payment") {
      onPaid(result.value.case);
      return;
    }
    setDiscount({ credits: result.value.credits, amount: result.value.amount, creditsToApply: 0 });
  };

  return (
    <Offer>
      <Note>{t("dueDiligence.creditDiscount.available", { count: credits.balance })}</Note>
      {/* None with half a credit: half credits only pay a DD whole, at its price in credits */}
      {discount.creditsToApply > 0 && (
        <ApplyButton type="button" onClick={apply} disabled={isApplying}>
          {t("dueDiligence.creditDiscount.apply", { count: discount.creditsToApply })}
        </ApplyButton>
      )}
      <Hint>{t("dueDiligence.creditDiscount.wholeOnly")}</Hint>
    </Offer>
  );
};
